/**
 * 기구 무대 아이 손 일정(KIDS) — 커널 시험(stage-answers · stage-sim-parity)과 개발 페이지(field-web /stage-check.html)가 같이 쓴다.
 * 파일 읽기가 없는 순수 모듈이라 브라우저에서도 불러온다(2026-10-07 stage-fixtures 에서 뗐다).
 */
import type { HandState } from "../src";

/** 아이 손(센서 손 패널 · 허브 버튼) 일정 — 틱 → 입력. 무대 화면에서 아이가 하는 일과 같다 */
export interface Kid {
  hand?: (s: number) => Partial<HandState>;
  /** 허브 왼쪽 버튼(리모컨 A)을 누르고 있는가 */
  left?: (s: number) => boolean;
  /** 허브 오른쪽 버튼(리모컨 B) */
  right?: (s: number) => boolean;
}
const tap = (at: number, s: number) => s >= at && s < at + 0.3;
export const KIDS: Record<string, Kid> = {
  // 시소(모터로 흔들기 · v4): 판 기울기는 놀이터(stage.tilt.legs) 몫 — 커널만으로는 판이 서 있다(machine-stage.test 가 정답으로 별 셋)
  "SP-D08": {},
  // 명함집 · 날갯짓: 손을 센서 앞에 1초 대고 3초 뗀다(거리 mm — 명함집 < 50 · 날갯짓 < 100)
  "SP-K03": { hand: (s) => ({ distance: s % 4 < 1 ? 20 : -1 }) },
  "SP-K04": { hand: () => ({ distance: 50 }) },
  // 자이로드롭: '거리 > 300' 까지 올리고 '거리 < 50' 까지 내린다(mm) — 3초에 손을 400mm 로 떼었다가 15초에 30mm 로 댄다
  // (2026-10-07 거리 mm 전환 전에는 cm 0~200 이라 300 을 못 넘어 별 하나에서 멈췄다)
  "SP-D10": { hand: (s) => ({ distance: s < 3 ? -1 : s < 15 ? 400 : 30 }) },
  // 원숭이: 허브를 20° 기울였다가 35° 로
  "SP-K10": { hand: (s) => ({ pitch: s < 10 ? 20 : 35 }) },
  // 휘핑기 · 공룡: 왼쪽 버튼 한 번
  "SP-K27": { left: (s) => tap(1, s) },
  "SP-K25": { left: (s) => tap(1, s) },
  // 헬기: 왼쪽 버튼 두 번(단계 2 = 600°/s)
  "SP-K20": { left: (s) => tap(1, s) || tap(2, s) },
  // ── 정적 기계 15종(2026-10-07) — 무대 화면 '센서 손' · 허브 단추로 아이가 하는 일 ──
  // 로봇 팔 · 집게 손: 왼쪽 단추로 시작 → 3초에 힘 센서 꾹(잡기) → 6초에 오른쪽 단추(놓기)
  "SP-B06": {
    hand: (s) => ({ force: s >= 3 && s < 4 ? 50 : 0 }),
    left: (s) => tap(1, s),
    right: (s) => tap(6, s),
  },
  "SP-K05": {
    hand: (s) => ({ force: s >= 3 && s < 5 ? 50 : 0 }),
    left: (s) => tap(1, s),
    right: (s) => tap(6, s),
  },
  // 집게 강화: 왼쪽 단추 → 3초에 힘 20(15 넘으면 멈추고 삐)
  "SP-K22": { hand: (s) => ({ force: s >= 3 ? 20 : 0 }), left: (s) => tap(1, s) },
  // 색상 분류기(C01 · E13): 빨강 · 없음 · 파랑 · 없음 카드를 2초씩
  "SP-C01": { hand: (s) => ({ color: [9, -1, 3, -1][Math.floor(s / 2) % 4]! }) },
  "SP-E13": { hand: (s) => ({ color: [9, -1, 3, -1][Math.floor(s / 2) % 4]! }) },
  // 팽이 발사기: 왼쪽 단추로 시작 · 4초에 힘 센서(빠르게) · 10초에 왼쪽 단추(멈추고 발사)
  "SP-K01": {
    hand: (s) => ({ force: s >= 4 && s < 4.5 ? 50 : 0 }),
    left: (s) => tap(1, s) || tap(10, s),
  },
  // 펜 플로터: 1초에 힘 센서 누르기
  "SP-K02": { hand: (s) => ({ force: s >= 1 && s < 1.5 ? 50 : 0 }) },
  // XY 플로터: 1초부터 흰 카드(10)
  "SP-K15": { hand: (s) => ({ color: s >= 1 ? 10 : -1 }) },
  // 스피로그래프: 왼쪽 단추를 1 · 13 · 25 · 37초에(무늬마다 10초 · 끝나고 1초 넘게 쉰 뒤 다음 무늬)
  "SP-K14": { left: (s) => tap(1, s) || tap(13, s) || tap(25, s) || tap(37, s) },
  // 펀치 로봇: 1.5초마다 힘 센서를 툭
  "SP-K16": { hand: (s) => ({ force: s % 1.5 < 0.3 ? 50 : 0 }) },
  // 배구: 3초마다 힘 센서(양쪽 팔) · 5초마다 왼쪽 단추(점수)
  "SP-K18": {
    hand: (s) => ({ force: s % 3 < 0.3 ? 50 : 0 }),
    left: (s) => s % 5 >= 1 && s % 5 < 1.3,
  },
  // 주사위: 왼쪽 단추로 시작 · 빨강 카드
  "SP-K19": { hand: () => ({ color: 9 }), left: (s) => tap(1, s) },
  // 공 굴리기: 왼쪽 단추로 시작 · 3초마다 빨강 카드 1초
  "SP-K24": { hand: (s) => ({ color: s % 3 < 1 ? 9 : -1 }), left: (s) => tap(1, s) },
  // 탁구공 발사기: 4초마다 힘 센서
  "SP-K29": { hand: (s) => ({ force: s % 4 < 0.3 ? 50 : 0 }) },
  // 핀볼: 1초에 힘 센서(시작) · 2초마다 빨강 공 0.5초
  "SP-K30": { hand: (s) => ({ force: s >= 1 && s < 1.5 ? 50 : 0, color: s % 2 < 0.5 ? 9 : -1 }) },
  // 악기: 빨 · 주 · 노 · 초 · 파 카드를 대고 누르기(도레미파솔)
  "SP-K26": {
    hand: (s) => {
      const i = Math.floor(s / 1.2);
      const colors = [9, 8, 7, 6, 3];
      return { color: colors[i % 5]!, force: s % 1.2 < 0.4 ? 50 : 0 };
    },
    left: (s) => s > 20 && s < 26,
  },
};
